import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, Save, RotateCcw } from "lucide-react";

const DEFAULTS = {
  min_hours_between_shifts: 8,
  max_hours_per_day: 8,
  max_shifts_per_week: 5,
  max_shifts_per_pay_period: 10,
  max_hours_per_pay_period: 80,
  max_consecutive_days: 5,
};

const FIELDS = [
  { key: "min_hours_between_shifts", label: "Minimum hours between shifts",     unit: "h", hint: "Rest period required between two consecutive shifts" },
  { key: "max_hours_per_day",         label: "Maximum hours in a 24-hour day", unit: "h", hint: "Total hours (shift + coverage) allowed per day" },
  { key: "max_shifts_per_week",       label: "Maximum shifts per week",         unit: "",  hint: "Sunday through Saturday" },
  { key: "max_shifts_per_pay_period", label: "Maximum shifts per pay period",   unit: "",  hint: "Over the 14-day pay period" },
  { key: "max_hours_per_pay_period",  label: "Maximum hours per pay period",   unit: "h", hint: "Over the 14-day pay period" },
  { key: "max_consecutive_days",      label: "Maximum consecutive days worked", unit: "",  hint: "Longest streak without a day off" },
];

export default function OTRulesPage({ facilityId, facilityLabel }) {
  const [rules, setRules] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/facilities/${facilityId}/ot-rules`);
      setRules({ ...DEFAULTS, ...data });
    } catch { setRules({ ...DEFAULTS }); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [facilityId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = FIELDS.reduce((acc, f) => {
        const v = rules[f.key];
        if (v !== "" && v !== null && !isNaN(Number(v))) acc[f.key] = Number(v);
        return acc;
      }, {});
      const { data } = await api.put(`/facilities/${facilityId}/ot-rules`, payload);
      setRules({ ...DEFAULTS, ...data });
      toast.success("OT rules saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const resetToDefaults = () => {
    setRules({ ...DEFAULTS });
    toast.message("Reset to defaults (not yet saved)");
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="ot-rules-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-lg font-display font-semibold">
            <AlertTriangle size={18} className="text-amber-600" />
            Overtime Rules
          </div>
          <div className="text-sm text-slate-500 mt-0.5">
            Configure per-facility thresholds. When assigning shifts, a banner warns
            you if the assignment breaks any of these rules{facilityLabel ? ` for ${facilityLabel}` : ""}.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetToDefaults} data-testid="ot-reset">
            <RotateCcw size={14} className="mr-1" /> Reset to defaults
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={save} disabled={saving || loading}
            data-testid="ot-save"
          >
            <Save size={14} className="mr-1" /> {saving ? "Saving…" : "Save rules"}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.key} className="border border-slate-200 rounded-xl p-3" data-testid={`ot-field-${f.key}`}>
            <Label className="text-sm font-medium text-slate-800">{f.label}</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input
                type="number" min="0" step="0.5"
                value={rules[f.key] ?? ""}
                onChange={(e) => setRules({ ...rules, [f.key]: e.target.value })}
                data-testid={`ot-input-${f.key}`}
                className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {f.unit && <span className="text-xs text-slate-500">{f.unit}</span>}
              <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-400 tabular-nums">
                default {DEFAULTS[f.key]}{f.unit}
              </span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5">{f.hint}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 text-amber-700 shrink-0" />
          <div>
            Rule violations show as <b>toast warnings</b> in the shift dialog after saving.
            The save is <b>not blocked</b> — managers can knowingly override — but the audit
            log captures the warning text.
          </div>
        </div>
      </div>
    </div>
  );
}
